import { message } from 'antd'
import dayjs from 'dayjs'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import {
  AssetCorrosionAnalysisData,
  BundleSubInspectionsData,
  ChecklistFindingsData,
  CoverSectionData,
  FindingsData,
  InspectionTeamMember,
  InspectionTeamMemberData,
  MainInfo,
  PressureTestSubInspectionsData,
  RecommendationsData,
  ReferenceDocument,
  ReferenceDocumentsData,
  VisualInspectionFormDataState,
  VisualInspectionRecommendation,
} from 'pages/reporting/reports/externalVisual/VisualReportData'
import { useCallback, useEffect, useState } from 'react'
import { GeckoReportingMetadata, SignatureData, SignoffState } from 'types'

export function deserializeDates<T>(data: T): T {
  if (data === null || data === undefined) return data

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => deserializeDates(item)) as unknown as T
  }

  if (typeof data === 'string') {
    // Check if it's a date string
    if (data.includes('Z')) {
      if (
        dayjs(
          data.slice(0, data.length - 1),
          'YYYY-MM-DDTHH:mm:ss.SSS',
          true
        ).isValid()
      ) {
        return dayjs(data) as unknown as T
      }
    }
  }

  // Handle objects
  if (typeof data === 'object') {
    // Process each property recursively
    const result: Record<string, unknown> = {}
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        result[key] = deserializeDates((data as Record<string, unknown>)[key])
      }
    }
    return result as unknown as T
  }

  return data
}

export function getSelectedFormData(
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[]
): VisualInspectionFormDataState | undefined {
  const currentEntry = metadataEntries.find(
    (entry) => entry.id === metadataEntryId
  )

  if (!currentEntry) {
    console.warn('Current metadata entry not found')
    return
  }

  const existingData = currentEntry.data
  const deserializedData = deserializeDates(existingData)

  const existingSectionInfo = deserializedData.sectionInfo as {
    mainInfo: MainInfo
    coverSectionData: CoverSectionData
    checklistFindingsData: ChecklistFindingsData
    assetCorrosionAnalysisData: AssetCorrosionAnalysisData
    findingsData: FindingsData
    recommendationsData: RecommendationsData
    pressureTestSubInspectionsData: PressureTestSubInspectionsData
    bundleSubInspectionsData: BundleSubInspectionsData
    inspectionTeamMemberData: InspectionTeamMemberData
    referenceDocumentsData: ReferenceDocumentsData
  }

  const newFormData: VisualInspectionFormDataState = {
    mainInfo: (existingSectionInfo.mainInfo as MainInfo) || ({} as MainInfo),
    coverSectionData:
      (existingSectionInfo.coverSectionData as CoverSectionData) ||
      ({} as CoverSectionData),
    checklistFindingsData:
      (existingSectionInfo.checklistFindingsData as ChecklistFindingsData) ||
      ({} as ChecklistFindingsData),
    assetCorrosionAnalysisData:
      (existingSectionInfo.assetCorrosionAnalysisData as AssetCorrosionAnalysisData) ||
      ({} as AssetCorrosionAnalysisData),
    findingsData:
      (existingSectionInfo.findingsData as FindingsData) ||
      ({} as FindingsData),
    recommendationsData:
      (existingSectionInfo.recommendationsData as RecommendationsData) ||
      ({} as RecommendationsData),
    pressureTestSubInspectionsData:
      (existingSectionInfo.pressureTestSubInspectionsData as PressureTestSubInspectionsData) ||
      ({} as PressureTestSubInspectionsData),
    bundleSubInspectionsData:
      (existingSectionInfo.bundleSubInspectionsData as BundleSubInspectionsData) ||
      ({} as BundleSubInspectionsData),
    inspectionTeamMemberData:
      (existingSectionInfo.inspectionTeamMemberData as InspectionTeamMemberData) ||
      ({} as InspectionTeamMemberData),
    referenceDocumentsData:
      (existingSectionInfo.referenceDocumentsData as ReferenceDocumentsData) ||
      ({} as ReferenceDocumentsData),

    // BaseFormData fields
    signoff: deserializedData.signoff,
    revisionMetadata: deserializedData.revisionMetadata || {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: deserializedData.lockState || {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: deserializedData.publishedPdfPath,
  }

  return newFormData
}

export const useFormData = (
  metadataEntryId: string,
  metadataEntries: readonly GeckoReportingMetadata[],
  isLoading: boolean
) => {
  const [formData, setFormData] = useState<VisualInspectionFormDataState>({
    mainInfo: {} as MainInfo,
    coverSectionData: {} as CoverSectionData,
    checklistFindingsData: {} as ChecklistFindingsData,
    assetCorrosionAnalysisData: {} as AssetCorrosionAnalysisData,
    findingsData: {} as FindingsData,
    recommendationsData: {} as RecommendationsData,
    pressureTestSubInspectionsData: {} as PressureTestSubInspectionsData,
    bundleSubInspectionsData: {} as BundleSubInspectionsData,
    inspectionTeamMemberData: {} as InspectionTeamMemberData,
    referenceDocumentsData: {} as ReferenceDocumentsData,
    signoff: undefined,
    revisionMetadata: {
      revisionNumber: 1,
      createdAt: new Date().toISOString(),
      isPublished: false,
    },
    lockState: {
      isLocked: false,
      startedSignoff: false,
    },
    publishedPdfPath: undefined,
  })

  const { rolodexPatch } = useRolodexEdit()

  useEffect(() => {
    const newFormData = getSelectedFormData(metadataEntryId, metadataEntries)

    if (newFormData) {
      setFormData(newFormData)
    }
  }, [isLoading, metadataEntries, metadataEntryId])

  const updateFormData = useCallback(
    (newData: Partial<VisualInspectionFormDataState>) => {
      setFormData((prev) => ({ ...prev, ...newData }))
    },
    [setFormData]
  )

  const handleCoverSectionFinish = useCallback(
    async (values: CoverSectionData) => {
      try {
        // Update both mainInfo and coverSectionData
        updateFormData({
          mainInfo: {
            asset: values.asset,
            assetId: values.assetId,
            circuitId: values.circuitId,
          },
          coverSectionData: values,
        })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                mainInfo: {
                  asset: values.asset,
                  assetId: values.assetId,
                  circuitId: values.circuitId,
                },
                coverSectionData: values,
              },
            },
          },
        ])
        message.success('Cover section saved successfully!')
      } catch {
        message.error('Failed to save cover section')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleChecklistFindingsFinish = useCallback(
    async (scope: string) => {
      try {
        const checklistData: ChecklistFindingsData = { scope }
        updateFormData({ checklistFindingsData: checklistData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                checklistFindingsData: checklistData,
              },
            },
          },
        ])
        message.success('Checklist findings saved successfully!')
      } catch {
        message.error('Failed to save checklist findings')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleAssetCorrosionAnalysisFinish = useCallback(
    async (analysis: string) => {
      try {
        const analysisData: AssetCorrosionAnalysisData = { analysis }
        updateFormData({ assetCorrosionAnalysisData: analysisData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                assetCorrosionAnalysisData: analysisData,
              },
            },
          },
        ])
        message.success('Asset corrosion analysis saved successfully!')
      } catch {
        message.error('Failed to save asset corrosion analysis')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleFindingsFinish = useCallback(
    async (findings: string) => {
      try {
        const findingsData: FindingsData = { findings }
        updateFormData({ findingsData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                findingsData,
              },
            },
          },
        ])
        message.success('Findings saved successfully!')
      } catch {
        message.error('Failed to save findings')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleRecommendationsFinish = useCallback(
    async (recommendations: VisualInspectionRecommendation[]) => {
      try {
        const recommendationsData: RecommendationsData = { recommendations }
        updateFormData({ recommendationsData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                recommendationsData,
              },
            },
          },
        ])
        message.success('Recommendations saved successfully!')
      } catch {
        message.error('Failed to save recommendations')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handlePressureTestSubInspectionsFinish = useCallback(
    async (pressureTestSubInspection: string) => {
      try {
        const pressureTestData: PressureTestSubInspectionsData = {
          pressureTestSubInspection,
        }
        updateFormData({ pressureTestSubInspectionsData: pressureTestData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                pressureTestSubInspectionsData: pressureTestData,
              },
            },
          },
        ])
        message.success('Pressure test sub-inspections saved successfully!')
      } catch {
        message.error('Failed to save pressure test sub-inspections')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleBundleSubInspectionsFinish = useCallback(
    async (bundleSubInspection: string) => {
      try {
        const bundleData: BundleSubInspectionsData = { bundleSubInspection }
        updateFormData({ bundleSubInspectionsData: bundleData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                bundleSubInspectionsData: bundleData,
              },
            },
          },
        ])
        message.success('Bundle sub-inspections saved successfully!')
      } catch {
        message.error('Failed to save bundle sub-inspections')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleInspectionTeamFinish = useCallback(
    async (inspectionTeamMembers: InspectionTeamMember[]) => {
      try {
        const teamData: InspectionTeamMemberData = { inspectionTeamMembers }
        updateFormData({ inspectionTeamMemberData: teamData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                inspectionTeamMemberData: teamData,
              },
            },
          },
        ])
        message.success('Inspection team saved successfully!')
      } catch {
        message.error('Failed to save inspection team')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleReferenceDocumentsFinish = useCallback(
    async (referenceDocuments: ReferenceDocument[]) => {
      try {
        const docsData: ReferenceDocumentsData = { referenceDocuments }
        updateFormData({ referenceDocumentsData: docsData })

        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              section_info: {
                referenceDocumentsData: docsData,
              },
            },
          },
        ])
        message.success('Reference documents saved successfully!')
      } catch {
        message.error('Failed to save reference documents')
      }
    },
    [metadataEntryId, updateFormData, rolodexPatch]
  )

  const handleSignoffFinish = useCallback(
    async (signatureData: SignatureData): Promise<SignoffState> => {
      try {
        const currentSignoff = formData.signoff || {
          lastRevision: 0,
        }
        const updatedSignoff: SignoffState = { ...currentSignoff }

        // Map role to signoff field
        switch (signatureData.role) {
          case 'supervisor':
            updatedSignoff.supervisorSignoff = signatureData
            break
          case 'reviewer':
            updatedSignoff.reviewerSignoff = signatureData
            // Increment revision if supervisor has already signed
            if (updatedSignoff.supervisorSignoff) {
              updatedSignoff.lastRevision =
                (updatedSignoff.lastRevision || 0) + 1
            }
            break
          default:
            // Handle other roles if needed
            break
        }

        // Update Rolodex first
        await rolodexPatch([
          {
            id: metadataEntryId,
            data: {
              signoff: updatedSignoff,
            },
          },
        ])

        // Then update local state
        updateFormData({ signoff: updatedSignoff })

        return updatedSignoff
      } catch (error) {
        console.error('Failed to save signoff:', error)
        throw error
      }
    },
    [formData.signoff, metadataEntryId, rolodexPatch, updateFormData]
  )

  return {
    formData,
    updateFormData,
    handleCoverSectionFinish,
    handleChecklistFindingsFinish,
    handleAssetCorrosionAnalysisFinish,
    handleFindingsFinish,
    handleRecommendationsFinish,
    handlePressureTestSubInspectionsFinish,
    handleBundleSubInspectionsFinish,
    handleInspectionTeamFinish,
    handleReferenceDocumentsFinish,
    handleSignoffFinish,
  }
}
